<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import * as imageValidationApi from '@/services/rest-api-sms/lib/image-validation/image-validation'

export default {
  page: {
    title: 'Sample Image Validation -> Foto Lampu (Headlamp)',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: 'Sample Image Validation -> Foto Lampu (Headlamp)',
      file: null,
      fileName: '',
      previewUrl: '',
      isLoading: false,
      result: null,
      errorMessage: '',
    }
  },
  computed: {
    resultFields() {
      return [
        { key: 'label', label: 'Field' },
        { key: 'value', label: 'Value' },
      ]
    },
    probabilitiesValid() {
      return this.result?.probabilities?.valid ?? '-'
    },
    resultRows() {
      if (!this.result) return []

      return [
        { label: 'Filename', value: this.result.filename },
        { label: 'Saved Path', value: this.result.saved_path },
        { label: 'Class Index', value: this.result.class_index },
        { label: 'Class Name', value: this.result.class_name },
        { label: 'Confidence', value: this.result.confidence },
        { label: 'Confidence Percent', value: this.result.confidence_percent },
        {
          label: 'Probability Invalid',
          value: this.result.probabilities ? this.result.probabilities.invalid : '-',
        },
        {
          label: 'Probability Valid',
          value: this.result.probabilities ? this.result.probabilities.valid : '-',
        },
      ]
    },
  },
  beforeDestroy() {
    this.revokePreviewUrl()
  },
  methods: {
    handleFileChange(event) {
      const [file] = event.target.files
      this.setFile(file)
    },
    handleDrop(event) {
      event.preventDefault()
      const [file] = event.dataTransfer.files
      this.setFile(file)
    },
    setFile(file) {
      this.result = null
      this.errorMessage = ''

      if (!file) {
        this.resetFile()
        return
      }

      if (!file.type.startsWith('image/')) {
        this.$toast.error('Please select an image file')
        this.resetFile()
        return
      }

      this.revokePreviewUrl()
      this.file = file
      this.fileName = file.name
      this.previewUrl = URL.createObjectURL(file)
    },
    resetFile() {
      this.revokePreviewUrl()
      this.file = null
      this.fileName = ''
      this.previewUrl = ''

      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },
    revokePreviewUrl() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
    },
    async submitImage() {
      if (!this.file) {
        this.$toast.warning('Please select an image first')
        return
      }

      this.isLoading = true
      this.errorMessage = ''
      this.result = null

      try {
        const response = await imageValidationApi.validateImage(this.file)

        if (response?.status && response.status >= 400) {
          this.errorMessage = response.errors || response.message || 'Image validation failed'
          this.$toast.error(this.errorMessage)
          return
        }

        this.result = response
        this.$toast.success('Image validation completed')
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error.message || 'Image validation failed'
        this.$toast.error(this.errorMessage)
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <b-row>
      <b-col lg="6">
        <b-card class="mc-card__setting" title="Upload Image">
          <div
            class="image-validation-dropzone mb-3"
            @drop="handleDrop"
            @dragover.prevent
          >
            <div v-if="previewUrl" class="text-center">
              <img :src="previewUrl" class="image-validation-preview mb-3" alt="Selected image preview" />
              <p class="mb-0 font-weight-bold">{{ fileName }}</p>
            </div>
            <div v-else class="text-center text-muted">
              <i class="mdi mdi-cloud-upload-outline image-validation-icon"></i>
              <p class="mb-1">Drop image here or choose a file</p>
              <small>Supported file: image/*</small>
            </div>
          </div>

          <b-form-file
            ref="fileInput"
            accept="image/*"
            placeholder="Choose an image..."
            browse-text="Browse"
            class="mb-3"
            @change="handleFileChange"
          />

          <div class="d-flex flex-wrap gap-2">
            <b-button variant="primary" :disabled="isLoading || !file" @click="submitImage">
              <b-spinner v-if="isLoading" small class="mr-1" />
              {{ isLoading ? 'Uploading...' : 'Upload & Validate' }}
            </b-button>
            <b-button variant="outline-secondary" :disabled="isLoading" @click="resetFile">
              Reset
            </b-button>
          </div>
        </b-card>
      </b-col>

      <b-col lg="6">
        <b-card class="mc-card__setting" title="Validation Result">
          <b-alert v-if="errorMessage" show variant="danger">
            {{ errorMessage }}
          </b-alert>

          <div v-if="result">
            <b-alert
              show
              :variant="Number(probabilitiesValid).toFixed(2) * 100 > 50 ? 'success' : 'danger'"
              class="mb-3"
            > <p class="text-muted mb-0">Probability Valid: <strong>{{ Number(probabilitiesValid).toFixed(2) * 100 }} %</strong></p>
            </b-alert>
          </div>

          <p v-else-if="!errorMessage" class="text-muted mb-0">
            Upload an image to see the endpoint response fields here.
          </p>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>

<style lang="scss" scoped>
.image-validation-dropzone {
  align-items: center;
  border: 2px dashed $mep-primary-100;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  min-height: 260px;
  padding: 24px;
}

.image-validation-preview {
  border-radius: 6px;
  max-height: 220px;
  max-width: 100%;
  object-fit: contain;
}

.image-validation-icon {
  display: block;
  font-size: 48px;
  line-height: 1;
}

.image-validation-json {
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
  max-height: 320px;
  overflow: auto;
  padding: 12px;
}
</style>
